import { apiClient, ApiResponse, v3Client } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { DocumentModel } from '@components/FileUpload/types';
import { ToastType } from '@components/Toast/Toast';
import { AddOrEditCommentPayload } from '@store/customers/customerDetails/types';
import { Comments } from '@typeDefinitions/comments';
import { TrackingDataPayload } from '@typeDefinitions/loadTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { copyToClipboard } from '@utils/copyToClipBoard';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { handleToast } from '@utils/utils';
import { TrackingCommentSchema } from '@views/order/components/customerTracking/CustomerTracking';
import { AxiosResponse } from 'axios';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { orderStore, setOrderStore } from '@store/orders/store';
import {
  AddFlagCommentPayload,
  CommentViewModel,
  EDIStatusCodesApiResponse,
  LoadCommentResponse,
  LoadEdiStatusUpdatePayload,
  TrackingResponse,
} from '@store/orders/types';

import { buildSourceTrackingConfig } from './serviceHelpers';

export async function deleteLoadComment(
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<boolean> {
  try {
    const response = await apiClient.delete<AxiosResponse, boolean>(
      `/Comment/Load/${commentId}`,
      buildSourceTrackingConfig(changeTrackingSource),
    );

    if (!response) {
      throw new Error('');
    }

    return response;
  } catch (error: unknown) {
    logError('deleteLoadComment', error as Error);
    throw createError(error, 'Failed to delete comment');
  }
}

export async function postLoadComment(
  comment: TrackingCommentSchema,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<LoadCommentResponse> {
  try {
    const response = await apiClient.post<
      LoadCommentResponse,
      LoadCommentResponse
    >(
      `/Comment/Load/${comment.loadId}`,
      comment,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if (response.id) {
      handleToast(ToastType.Success, 'Comment posted');
    } else {
      handleToast(ToastType.Error, 'Failed to post comment');
    }
    return response;
  } catch (error: unknown) {
    logError('postLoadComment', error as Error);
    throw createError(error, 'Failed to post load comment');
  }
}

export async function putLoadCommentVisibility(
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: V4ApiResponse<null> = await apiClient.put(
      `/Comment/Load/Visibility/${commentId}`,
      undefined,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if (response.isSuccess) {
      handleToast(ToastType.Success, 'Comment visibility updated');
    } else {
      handleToast(ToastType.Error, 'Failed to update comment visibility');
    }
    return response;
  } catch (error: unknown) {
    logError('putLoadCommentVisibility', error as Error);
    throw createError(error, 'Failed to upload load comment');
  }
}

export async function fetchOrderDocuments(
  orderId: string,
): Promise<DocumentModel[]> {
  try {
    const response = await apiClient.get<ApiResponse<DocumentModel[]>>(
      `/document/GetOrderDocuments/${orderId}`,
    );
    if ('value' in response) {
      return response.value as DocumentModel[];
    }
    throw new Error('Failed to fetch order documents');
  } catch (error: unknown) {
    logError('fetchOrderDocuments', error as Error);
    throw createError(error, 'Failed to fetch order documents');
  }
}

export async function postOrderComment(
  payload: AddOrEditCommentPayload,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: V4ApiResponse<Comments> = await apiClient.post(
      'Order/saveordercomments',
      payload,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    return response;
  } catch (error: unknown) {
    logError('postOrderComment', error as Error);
    const e = createError(error, 'Failed to post order comment');
    handleToast(ToastType.Error, e.message);
  }
}

export async function deleteOrderComment(
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const res: V4ApiResponse<null> = await apiClient.delete(
      `Order/deleteComment/${commentId}`,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if (res.isSuccess === false) {
      throw res.errors;
    }
    return res.isSuccess;
  } catch (error: unknown) {
    logError('deleteOrderComment', error as Error);
    throw createError(error, 'Failed to delete order comment');
  }
}

export async function resendEDI(id: string | undefined) {
  try {
    await v3Client.post(
      `EDI/Resend/${id}`,
      { id },
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.ediUpdatesResend,
      ]),
    );
  } catch (error: unknown) {
    logError('resendEDI', error as Error);
    throw createError(error, 'Failed to resend the EDI');
  }
}

export async function fetchEdiChangeLogs(
  id: string | undefined,
): Promise<IOrderViewModel> {
  try {
    const response = await v3Client.get(`EDI/GetChangeDetails/${id}?id=${id}`);
    return response as unknown as IOrderViewModel;
  } catch (error: unknown) {
    logError('fetchEdiChangeLogs', error as Error);
    throw new Error(
      `Failed to fetch Edi transaction logs: ${
        error instanceof Error ? error.message : error
      }`,
    );
  }
}

export async function enableTracking(
  data: TrackingDataPayload,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.trackingResendTrackingInstallation,
): Promise<TrackingResponse | undefined> {
  try {
    const response = await apiClient.post(
      '/Tracking/Begin',
      data,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const apiResponse = response as unknown as ApiResponse<TrackingResponse>;

    if (Boolean(apiResponse.value.success)) {
      return apiResponse.value;
    }

    throw new Error('There was an unknown error initiating tracking');
  } catch (error: unknown) {
    logError('enableTracking', error as Error);
    throw createError(error, 'Failed to enable tracking');
  }
}

export async function copyShipperUrl(loadId: number) {
  const load = orderStore.order.loads.find((l) => l.id === loadId);
  if (!load || !load.loadTrackingRequest) {
    handleToast(ToastType.Error, 'Failed to obtain shipper URL');
    return;
  }

  const shipperLink = load.loadTrackingRequest.shipperLink;
  if (!shipperLink) {
    handleToast(ToastType.Error, 'Failed to obtain shipper URL');
    return;
  }

  const success = await copyToClipboard(shipperLink);
  if (success) {
    handleToast(ToastType.Success, 'Shipper URL copied to clipboard');
  } else {
    handleToast(ToastType.Error, 'Failed to copy Shipper URL');
  }
}

export async function disableTracking(
  loadId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.trackingDisableTracking,
) {
  try {
    const response = await apiClient.post(
      `/Tracking/${loadId}`,
      undefined,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    const apiResponse = response as unknown as ApiResponse<boolean>;
    if (apiResponse.isSuccess) {
      handleToast(ToastType.Success, 'Load Tracking Disabled');
      setOrderStore('order', 'loads', orderStore.activeTab.index, {
        trackingEnabled: false,
        trackingServiceId: null,
      });
    } else {
      handleToast(ToastType.Error, apiResponse.errors?.join(', ') ?? '');
    }
  } catch (error: unknown) {
    logError('disableTracking', error as Error);
    throw createError(error, 'Failed to disable tracking');
  }
}

export const sendEmail = async (
  from: string[],
  sender: string,
  to: string[],
  subject: string,
  message: string,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const resp = await apiClient.post(
      'Email/Send',
      {
        From: sender,
        CC: from,
        Body: message,
        Subject: subject,
        To: to,
      },
      buildSourceTrackingConfig(changeTrackingSource),
    );
    handleToast(ToastType.Success, 'Email sent to customer');
    return (resp as unknown as { success: boolean }).success;
  } catch (e) {
    logError('sendEmail', e as Error);
    handleToast(ToastType.Error, 'Unable to send email');
    return false;
  }
};

export const sendText = async (
  to: string,
  message: string,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const resp = await apiClient.post(
      'Email/SendText',
      {
        number: to,
        message: message,
      },
      buildSourceTrackingConfig(changeTrackingSource),
    );
    handleToast(ToastType.Success, 'Text message sent to Accounting');
    return (resp as unknown as { success: boolean }).success;
  } catch (e) {
    logError('sendText', e as Error);
    handleToast(
      ToastType.Error,
      `Unable to send text to ${formatPhoneNumber(to)}`,
    );
    return false;
  }
};

export async function getFlagComment(commentId: number) {
  try {
    const response: V4ApiResponse<Record<string, string | number>[]> =
      await apiClient.get(`Flag/Comment/${commentId}`);
    return response.value as unknown as Record<string, string | number>[];
  } catch (error: unknown) {
    logError('getFlagComment', error as Error);
    throw createError(error, 'Failed to fetch comment on flag');
  }
}

export async function addFlagComment(
  payload: AddFlagCommentPayload,
  cb?: () => void,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const trackingConfig = buildSourceTrackingConfig(changeTrackingSource);
    const response = await apiClient.get(`Flag/Comment/${payload.id}`, {
      ...trackingConfig,
      params: payload,
    });
    return response.data as unknown as Record<string, string | number>;
  } catch (error: unknown) {
    logError('addFlagComment', error as Error);
    throw createError(error, 'Failed to add comment on flag');
  } finally {
    cb && cb();
  }
}

export async function addFlagCommentV4(
  id: number,
  message: string,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response = await apiClient.post(
      'flag/comment',
      {
        id,
        description: message,
      },
      buildSourceTrackingConfig(changeTrackingSource),
    );
    return response as unknown as CommentViewModel;
  } catch (error: unknown) {
    logError('addFlagComment', error as Error);
    throw createError(error, 'Failed to add comment on flag');
  }
}

export async function deleteFlagComment(
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response = await apiClient.delete(
      `flag/DeleteOrderFlagComment/${commentId}`,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    return response as unknown as Record<string, string | number>;
  } catch (error: unknown) {
    logError('deleteFlagComment', error as Error);
    throw createError(error, 'Failed to delete comment on flag');
  }
}

export async function fetchEDIStatusCodes(
  id: number,
): Promise<EDIStatusCodesApiResponse> {
  try {
    const response: V4ApiResponse<EDIStatusCodesApiResponse> =
      await apiClient.get(`edi/StatusCodes/${id}`);
    return response.value as unknown as EDIStatusCodesApiResponse;
  } catch (error: unknown) {
    logError('fetchEDIStatusCodes', error as Error);
    throw createError(error, 'Failed to fetch EDI status codes');
  }
}

export async function sendEDIUpdate(
  id: number,
  payload: LoadEdiStatusUpdatePayload,
): Promise<Record<string, string | number | boolean>> {
  try {
    const response = await v3Client.post(
      `EDI/SendUpdate/${id}`,
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.ediUpdatesSendUpdate,
      ]),
    );
    return response as unknown as Record<string, string | number | boolean>;
  } catch (error: unknown) {
    logError('sendEDIUpdate', error as Error);
    throw createError(error, 'Failed to send EDI Update');
  }
}

export async function postNextCheckCall(
  loadId: number,
  nextCheckCallDateUtc: string,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .orderDetails.trackingScheduleCheckCall,
): Promise<boolean> {
  try {
    const response = await apiClient.post(
      'load/nextcheckcall',
      {
        loadId,
        nextCheckCallDateUtc,
      },
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );

    return response as unknown as boolean;
  } catch (error: unknown) {
    logError('postNextCheckCall', error as Error);
    throw createError(error, 'Failed to update next check call');
  }
}
